import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FetchStatus } from '../types/common';

// Type pour les sessions de groupe
export type GroupSessions = Record<string, string[]>; // ex: { G1: ["2024-01-01", ...], G2: [...] }

export interface UserStory {
  id: number;
  code: string;
  description: string;
  story_points: number;
  position?: number;
  // Branches où la story est attendue (absent = toutes les branches du cycle)
  branches?: string[];
}

// Cycle en base (page Admin Cycles), calé sur une ou plusieurs branches ; plusieurs cycles peuvent
// partager une branche (sous-cycles de TP-Projet)
export interface Cycle {
  id: number;
  name: string;
  short_name: string;
  position?: number;
  branches: string[];
  binome_only_branches?: string[];
  solo_fallback?: Record<string, string>;
  default_dates_from_sessions?: { start: string; end: string } | null;
  user_stories: UserStory[];
  // Dates effectives (saisie admin, sinon calculées depuis les séances) — voir page Admin Cycles
  start_date?: string | null;
  end_date?: string | null;
  start_source?: 'manual' | 'sessions' | null;
  end_source?: 'manual' | 'sessions' | null;
  computed_start_date?: string | null;
  computed_end_date?: string | null;
}

export interface CourseConfig {
  id: number;
  full_name: string;
  cycles: Cycle[];
}

export interface CycleScoreMean {
  [cycleId: string]: number | null;
}

export interface CourseStats {
  cycle_score_mean: CycleScoreMean;
  global_score?: number | null;
  // Moyennes par groupe TD : { [cycleId]: { [td]: moyenne } } et { [td]: moyenne du score global }
  td_cycle_mean?: { [cycleId: string]: { [td: string]: number | null } };
  td_global_mean?: { [td: string]: number | null };
}

export interface AppDataState {
  version: string;
  maintenanceMode: boolean;
  // Données de configuration de cours
  courseConfig: CourseConfig | null;
  courseConfigStatus: FetchStatus;
  courseConfigError: string;
  // Données de sessions de groupe
  groupSessions: GroupSessions | null;
  groupSessionsStatus: FetchStatus;
  groupSessionsError: string;
  // Ajout des stats de cycle
  courseStats: CourseStats | null;
  courseStatsStatus: FetchStatus;
  courseStatsError: string;
}

const initialState: AppDataState = {
  version: '1.0.0',
  maintenanceMode: false,
  courseConfig: null,
  courseConfigStatus: FetchStatus.INITIAL,
  courseConfigError: '',
  groupSessions: null,
  groupSessionsStatus: FetchStatus.INITIAL,
  groupSessionsError: '',
  courseStats: null,
  courseStatsStatus: FetchStatus.INITIAL,
  courseStatsError: '',
};

const appDataSlice = createSlice({
  name: 'appData',
  initialState,
  reducers: {
    setVersion: (state, action: PayloadAction<string>) => {
      state.version = action.payload;
    },
    setMaintenanceMode: (state, action: PayloadAction<boolean>) => {
      state.maintenanceMode = action.payload;
    },
    // Gestion du fetch groupSessions
    fetchGroupSessionsStart(state) {
      state.groupSessionsStatus = FetchStatus.LOADING;
      state.groupSessionsError = '';
    },
    fetchGroupSessionsSuccess(state, action: PayloadAction<GroupSessions>) {
      state.groupSessionsStatus = FetchStatus.SUCCESS;
      state.groupSessions = action.payload;
      state.groupSessionsError = '';
    },
    fetchGroupSessionsFailure(state, action: PayloadAction<string>) {
      state.groupSessionsStatus = FetchStatus.ERROR;
      state.groupSessionsError = action.payload;
    },
    clearGroupSessionsError(state) {
      state.groupSessionsError = '';
      state.groupSessionsStatus = FetchStatus.INITIAL;
    },
    // Reducers pour courseConfig
    fetchCourseConfigStart(state) {
      state.courseConfigStatus = FetchStatus.LOADING;
      state.courseConfigError = '';
    },
    fetchCourseConfigSuccess(state, action: PayloadAction<CourseConfig>) {
      state.courseConfigStatus = FetchStatus.SUCCESS;
      state.courseConfig = action.payload;
      state.courseConfigError = '';
    },
    fetchCourseConfigFailure(state, action: PayloadAction<string>) {
      state.courseConfigStatus = FetchStatus.ERROR;
      state.courseConfigError = action.payload;
    },
    clearCourseConfigError(state) {
      state.courseConfigError = '';
      state.courseConfigStatus = FetchStatus.INITIAL;
    },
    // Gestion du fetch courseStats
    fetchCourseStatsStart(state) {
      state.courseStatsStatus = FetchStatus.LOADING;
      state.courseStatsError = '';
    },
    fetchCourseStatsSuccess(state, action: PayloadAction<CourseStats>) {
      state.courseStatsStatus = FetchStatus.SUCCESS;
      state.courseStats = action.payload;
      state.courseStatsError = '';
    },
    fetchCourseStatsFailure(state, action: PayloadAction<string>) {
      state.courseStatsStatus = FetchStatus.ERROR;
      state.courseStatsError = action.payload;
    },
    clearCourseStatsError(state) {
      state.courseStatsError = '';
      state.courseStatsStatus = FetchStatus.INITIAL;
    },
  }
});

export const {
  setVersion,
  setMaintenanceMode,
  fetchCourseConfigStart,
  fetchCourseConfigSuccess,
  fetchCourseConfigFailure,
  clearCourseConfigError,
  // Actions pour groupSessions
  fetchGroupSessionsStart,
  fetchGroupSessionsSuccess,
  fetchGroupSessionsFailure,
  clearGroupSessionsError,
  // Actions pour courseStats
  fetchCourseStatsStart,
  fetchCourseStatsSuccess,
  fetchCourseStatsFailure,
  clearCourseStatsError,
} = appDataSlice.actions;

// Sélecteurs pour accéder aux sessions de groupe
export const selectGroupSessions = (state: { appData: AppDataState }) => state.appData.groupSessions;
export const selectGroupSessionsStatus = (state: { appData: AppDataState }) => state.appData.groupSessionsStatus;
export const selectGroupSessionsError = (state: { appData: AppDataState }) => state.appData.groupSessionsError;

// Sélecteurs pour courseStats
export const selectCourseStats = (state: { appData: AppDataState }) => state.appData.courseStats;
export const selectCourseStatsStatus = (state: { appData: AppDataState }) => state.appData.courseStatsStatus;
export const selectCourseStatsError = (state: { appData: AppDataState }) => state.appData.courseStatsError;

export const appDataReducer = appDataSlice.reducer;

// Sélecteurs
export const selectAppData = (state: any) => state.appData;
export const selectVersion = (state: any) => state.appData?.version;
export const selectMaintenanceMode = (state: any) => state.appData?.maintenanceMode;
export const selectCourseConfig = (state: any) => state.appData?.courseConfig;
export const selectCourseConfigStatus = (state: any) => state.appData?.courseConfigStatus;
export const selectCourseConfigError = (state: any) => state.appData?.courseConfigError;
